import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { isNative } from '../services/platform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL_MS = 3000;

/**
 * Push2FAResponder
 *
 * Mounts a background poller that asks /api/2fa/push/pending every 3s while
 * the user is authenticated. When a PENDING challenge is found, shows a
 * full-screen modal asking the user to type the matching 2-digit number to
 * approve the login on the web. The user can also deny.
 *
 * Polling is enabled on web AND native (so the user can approve a desktop
 * login from a browser tab on their phone as well, even before FCM is wired).
 */
export default function Push2FAResponder({ user }) {
  const [challenge, setChallenge] = useState(null); // { id, purpose, created_at, ip_address }
  const [number, setNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) return;
    let stopped = false;

    const tick = async () => {
      if (challenge) return; // already showing a challenge
      try {
        const res = await axios.get(`${API}/2fa/push/pending`);
        const list = Array.isArray(res.data) ? res.data : [];
        if (!stopped && list.length > 0) {
          setChallenge(list[0]);
          setNumber('');
          setError('');
        }
      } catch {
        // ignore — user might be logged out
      }
    };

    tick();
    const intervalId = setInterval(tick, POLL_INTERVAL_MS);
    return () => { stopped = true; clearInterval(intervalId); };
  }, [user, challenge]);

  const submitApprove = async (e) => {
    e?.preventDefault?.();
    if (!challenge || number.length < 2) return;
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/2fa/push/challenge/${challenge.id}/approve`, { number_code: number });
      toast.success('Anmeldung bestätigt');
      setChallenge(null);
      setNumber('');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Fehler';
      setError(detail);
      // If wrong number → backend marked denied → clear modal
      if (err.response?.status === 401) {
        setTimeout(() => { setChallenge(null); setNumber(''); }, 1500);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submitDeny = async () => {
    if (!challenge) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/2fa/push/challenge/${challenge.id}/deny`, {});
      toast.info('Anfrage abgelehnt');
    } catch { /* noop */ }
    setChallenge(null);
    setNumber('');
    setSubmitting(false);
  };

  if (!challenge) return null;

  const purposeLabel = challenge.purpose === 'LOGIN' ? 'Anmeldung' :
    challenge.purpose === 'STEP_UP' ? 'Sensibler Zugriff' : 'Bestätigung';

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      data-testid="push-2fa-responder-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-5">
        <div className="text-center">
          <div
            className="inline-flex items-center justify-center mb-3 rounded-full"
            style={{
              width: 64,
              height: 64,
              background: 'linear-gradient(145deg, #C5A059, #B0904F)',
            }}
          >
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h3 className="text-lg font-bold text-primary">Neue Anfrage: {purposeLabel}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Bestätige diese Anfrage durch Eingabe der auf dem anderen Gerät angezeigten Zahl.
          </p>
          {challenge.ip_address && (
            <p className="text-xs text-muted-foreground mt-2">Anfrage von IP: {challenge.ip_address}</p>
          )}
        </div>

        <form onSubmit={submitApprove} className="space-y-4">
          <input
            type="tel"
            inputMode="numeric"
            pattern="\d{2}"
            maxLength={2}
            autoFocus
            value={number}
            onChange={(e) => setNumber(e.target.value.replace(/\D/g, '').slice(0, 2))}
            className="w-full text-center font-mono font-bold rounded-xl border-2 focus:border-[#C5A059] outline-none transition"
            style={{
              fontSize: '4rem',
              padding: '1rem 0',
              letterSpacing: '0.5rem',
              borderColor: error ? '#dc2626' : '#E4E4E7',
            }}
            placeholder="00"
            data-testid="push-2fa-responder-input"
          />
          {error && (
            <p className="text-center text-sm text-red-600" data-testid="push-2fa-responder-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={submitting || number.length !== 2}
            className="btn-primary w-full"
            data-testid="push-2fa-responder-approve"
          >
            {submitting ? 'Wird bestätigt…' : 'Bestätigen'}
          </button>
          <button
            type="button"
            onClick={submitDeny}
            disabled={submitting}
            className="w-full py-2.5 text-sm text-gray-500 hover:text-red-600 transition-colors"
            data-testid="push-2fa-responder-deny"
          >
            Ablehnen
          </button>
        </form>
      </div>
    </div>
  );
}
